import * as React from 'react'
import { useMemo } from 'react'
import { Button, Divider, mergeClasses, Text, Tooltip } from '@fluentui/react-components'
import { Link } from 'react-router-dom'
import {
    ArrowUndoFilled,
    CheckmarkCircleRegular,
    CheckmarkFilled,
    DocumentArrowDownRegular,
    DocumentPdfRegular,
    DraftsRegular,
    HistoryFilled,
    MailAddRegular,
    NewRegular,
    Payment16Filled,
    Payment16Regular,
} from '@fluentui/react-icons'
import { InvoiceLineItemProps, useItemStyles } from './InvoiceOverview'
import { useTranslation } from 'react-i18next'
import { useOverviewStyles } from '../../BaseOverview/BaseOverview'
import { InvoiceStatusLabels } from '../../../components/TagPicker/InvoiceStatusPicker'
import AccountingSubmissionPreview from '../../../components/Preview/AccountingSubmissionPreview'
import FormattedDate from '../../../components/Date/FormattedDate'
import { InvoiceStatus, InvoiceType } from '../../../config/finance/invoiceConfig'
import useViewport, { Viewport } from '../../../hooks/useViewport'
import PersonAvatar from '../../../components/PersonAvatar'
import { DateFormats } from '../../../helpers/dateHelper'
import MoneyText from '../../../components/Text/Currency/MoneyText'
import InvoiceNoteDialog from '../../../components/Dialog/InvoiceNote/InvoiceNoteDialog'
import useAxios from '../../../hooks/useAxios'
import { useQueue } from '../../../hooks/useQueue'
import { stealthClick } from '../../../components/StealthLink'
import useUnits from '../../../hooks/useUnits'
import useSubmitDocumentGenerationRequest from '../../../hooks/finance/report/useSubmitDocumentGenerationRequest'
import { DocumentGenerationStatusData, DocumentGenerationType } from '../../../hooks/document/useDocumentGenerationHandler'
import { InvoiceParams } from '../../../hooks/finance/invoice/useSubmitInvoiceDocumentGeneration'
import { QueueStatus } from '../../../hooks/useQueueStateHandler'
import { getDriveItemDownload } from '../../../helpers/documentHelper'
import useComposeInvoiceOutlookMessage from '../../../hooks/finance/invoice/useComposeInvoiceOutlookMessage'
import { isOfficeInitialized } from '../../../helpers/outlookHelper'
import useCurrencies from '../../../hooks/useCurrencies'
import { formatDecimals, formatNumber, parseNumber } from '../../../helpers/numberHelper'
import FormattedDateRange from '../../../components/Date/FormattedDateRange'
import { DocumentOpenType } from '../../../context/GlobalContext'
import { useSettings } from '../../../hooks/useSettings'

function getStatusIcon(status: InvoiceStatus) {
    switch (status) {
        case InvoiceStatus.New:
            return <NewRegular />
        case InvoiceStatus.Draft:
            return <DraftsRegular />
        case InvoiceStatus.Final:
            return <CheckmarkFilled />
        case InvoiceStatus.Settled:
            return <ArrowUndoFilled />
        case InvoiceStatus.Credited:
            return <HistoryFilled />
        default:
            return <CheckmarkCircleRegular />
    }
}

function InvoiceListItemTitle(props: InvoiceLineItemProps) {
    const axios = useAxios()
    const composeOutlookMessage = useComposeInvoiceOutlookMessage()
    const overviewStyles = useOverviewStyles()
    const { t: translate } = useTranslation()
    const queue = useQueue()
    const settings = useSettings()

    const generatePdf = useSubmitDocumentGenerationRequest<InvoiceParams, DocumentGenerationStatusData>(DocumentGenerationType.Invoice)

    return (
        <div className={overviewStyles.flexJustifyBetween}>
            <Text size={200}>
                {props.item.recipient || props.item.files
                    ? props.item.recipient?.name || `Gedeelde declaratie ${props.item.files?.[0]?.reference} - ${props.item.name}`
                    : undefined}
            </Text>
            <div>
                {![InvoiceType.Split].includes(props.item.type) && !props.item.pdfId && (
                    <Tooltip relationship={'label'} content={translate('invoice.action.generatepdf')} withArrow>
                        <Button
                            aria-label={'hover'}
                            icon={<DocumentPdfRegular />}
                            appearance="secondary"
                            size={'small'}
                            disabled={queue.has(props.item.id)}
                            onClick={() => {
                                generatePdf({ invoiceId: props.item.id }, props.item.number, {
                                    [QueueStatus.Finished]: data => {
                                        props.onUpdateItem(props.item)
                                        stealthClick(
                                            {
                                                id: data.status.driveItem.id,
                                                item: data.status.driveItem,
                                                documentsOpen: settings.documentsOpen ?? DocumentOpenType.Open,
                                            },
                                            axios,
                                        )
                                    },
                                })
                            }}
                        />
                    </Tooltip>
                )}
                {![InvoiceType.Split].includes(props.item.type) && props.item.pdfId && (
                    <Tooltip relationship={'label'} content={translate('invoice.action.downloadpdf')} withArrow>
                        <Button
                            aria-label={'hover'}
                            icon={<DocumentArrowDownRegular />}
                            appearance="secondary"
                            size={'small'}
                            disabled={queue.has(props.item.id)}
                            onClick={() =>
                                stealthClick({ id: props.item.pdfId, documentsOpen: settings.documentsOpen ?? DocumentOpenType.Open }, axios)
                            }
                        />
                    </Tooltip>
                )}
                {isOfficeInitialized() &&
                    ![InvoiceType.Split].includes(props.item.type) &&
                    props.item.pdfId &&
                    props.item.status === InvoiceStatus.Final && (
                        <Tooltip relationship={'label'} content={translate('invoice.action.sendemail')} withArrow>
                            <Button
                                aria-label={'hover'}
                                icon={<MailAddRegular />}
                                appearance="secondary"
                                size={'small'}
                                disabled={queue.has(props.item.id)}
                                onClick={() => {
                                    getDriveItemDownload(props.item.pdfId, axios).then(pdf =>
                                        composeOutlookMessage(props.item.id, props.item.recipient?.email || '', pdf),
                                    )
                                }}
                            />
                        </Tooltip>
                    )}
            </div>
        </div>
    )
}

function InvoiceListItemTurnover(props: InvoiceLineItemProps) {
    const overviewStyles = useOverviewStyles()
    const itemStyles = useItemStyles()
    const { getCurrency } = useCurrencies()

    return (
        <Tooltip
            content={
                <div className={itemStyles.turnoverContainer}>
                    <Text>Subtotaal</Text>
                    <Text>{getCurrency(props.item.price.currency).symbol}</Text>
                    <MoneyText amount={props.item.price.subtotal} hideCurrency={true} textProps={{ size: 300 }} align="right" />
                    <Text>BTW</Text>
                    <Text>{getCurrency(props.item.price.currency).symbol}</Text>
                    <MoneyText
                        amount={formatNumber(
                            props.item.price.vat.map(vat => parseNumber(vat.value, 'en')).reduce((a, b) => a + b, 0.0),
                            '0.00',
                            'en',
                        )}
                        hideCurrency={true}
                        textProps={{ size: 300 }}
                        align="right"
                    />
                    <Text>Totaal</Text>
                    <Text>{getCurrency(props.item.price.currency).symbol}</Text>
                    <MoneyText amount={props.item.price.total} hideCurrency={true} textProps={{ size: 300 }} align="right" />
                    <Divider vertical={false} />
                    <Divider vertical={false} />
                    <Divider vertical={false} />
                    <Text>Betaald</Text>
                    <Text>{getCurrency(props.item.price.currency).symbol}</Text>
                    <MoneyText amount={props.item.paidPrice} hideCurrency={true} textProps={{ size: 300 }} align="right" />
                    <Text>Nog te betalen</Text>
                    <Text>{getCurrency(props.item.outstandingPrice?.currency)?.symbol}</Text>
                    <MoneyText amount={props.item.outstandingPrice?.total} hideCurrency={true} textProps={{ size: 300 }} align="right" />
                    {props.item.type === InvoiceType.Deposit && (
                        <>
                            <Divider vertical={false} appearance={'strong'} />
                            <Divider vertical={false} appearance={'strong'} />
                            <Divider vertical={false} appearance={'strong'} />
                            <Text>Nog te verrekenen</Text>
                            <Text>{getCurrency(props.item.unsettledPrice?.currency)?.symbol}</Text>
                            <MoneyText amount={props.item.unsettledPrice?.total} hideCurrency={true} textProps={{ size: 300 }} align="right" />
                        </>
                    )}
                </div>
            }
            relationship={'description'}
            positioning={'above-end'}
            withArrow
        >
            <div className={mergeClasses(overviewStyles.flexJustifyEnd, overviewStyles.alignItemsCenter, overviewStyles.flexGapXS)}>
                <MoneyText
                    value={{
                        currency: props.item.price.currency,
                        amount: props.item.price.total,
                    }}
                    textProps={{
                        size: 300,
                    }}
                    align="right"
                />
                {props.item.isPartiallyPaid && <Payment16Regular />}
                {props.item.isPaid && <Payment16Filled />}
                {!props.item.isPartiallyPaid && !props.item.isPaid && <div className={itemStyles.listInvoiceItemPaidIcon}></div>}
            </div>
        </Tooltip>
    )
}

export default function InvoiceListItem(props: InvoiceLineItemProps) {
    const overviewStyles = useOverviewStyles()
    const itemStyles = useItemStyles()

    const { getCurrency } = useCurrencies()
    const { t: translate } = useTranslation()
    const { viewport, isViewport } = useViewport()
    const { units } = useUnits()

    const listItemSmall = useMemo<boolean>(() => isViewport('<', Viewport.XL), [viewport])
    const listItemMedium = useMemo<boolean>(() => isViewport('>=', Viewport.XXL), [viewport])
    const statusIcon = getStatusIcon(props.item.status)

    if (listItemSmall) {
        return (
            <>
                <PersonAvatar name={props.item.fileOwner.fullName} color={'colorful'} size={24} />
                <div className={itemStyles.listInvoiceItemTitle}>
                    <InvoiceListItemTitle {...props} />
                    <Link className={overviewStyles.alignItemsEnd} to={`/invoice/${props.item.id}/edit`}>
                        {props.item.number}
                    </Link>
                </div>

                <div className={overviewStyles.flexJustifyBetween}>
                    <Tooltip
                        relationship={'label'}
                        content={
                            <div>
                                {translate(InvoiceStatusLabels.get(props.item.status)?.name, {
                                    count: 1,
                                    currency: getCurrency(props.item.unsettledPrice?.currency)?.symbol || props.item.unsettledPrice?.currency,
                                    amount:
                                        formatDecimals(
                                            props.item.unsettledPrice?.total || 0,
                                            getCurrency(props.item.unsettledPrice?.currency)?.decimals || 2,
                                            'en',
                                            'nl-nl',
                                        ) || 0,
                                })}
                            </div>
                        }
                        withArrow
                    >
                        <div className={overviewStyles.centerItems}>{statusIcon}</div>
                    </Tooltip>
                    {units.length > 0 && ![InvoiceType.Split].includes(props.item.type) ? (
                        <AccountingSubmissionPreview item={props.item} reset={() => props.onUpdateItem(props.item)} />
                    ) : (
                        <div></div>
                    )}
                </div>

                <div className={itemStyles.listInvoiceItemSmallBottomRow}>
                    <InvoiceNoteDialog invoice={props.item} onSubmit={value => (props.item.note = value)} />
                    <div>
                        <FormattedDate date={props.item.date} format={DateFormats.Full} />
                    </div>
                    <Tooltip relationship={'label'} content={props.item.files?.map(file => `${file.reference}: ${file.name}`).join(', ')} withArrow>
                        <Link key={props.item.id} to={`/file/${props.item.files?.[0]?.id}/time`} className={overviewStyles.alignItemsStart}>
                            <Text size={200}>
                                {props.item.files?.[0]?.reference} {props.item.files?.length > 1 ? `+${props.item.files?.length - 1}` : ''}
                            </Text>
                        </Link>
                    </Tooltip>
                    <InvoiceListItemTurnover {...props} />
                </div>
            </>
        )
    }

    return (
        <>
            <PersonAvatar name={props.item.fileOwner.fullName} color={'colorful'} size={24} />
            <Tooltip
                relationship={'label'}
                content={
                    <div>
                        {translate(InvoiceStatusLabels.get(props.item.status)?.name, {
                            count: 1,
                            currency: getCurrency(props.item.unsettledPrice?.currency)?.symbol || props.item.unsettledPrice?.currency,
                            amount:
                                formatDecimals(
                                    props.item.unsettledPrice?.total || 0,
                                    getCurrency(props.item.unsettledPrice?.currency)?.decimals || 2,
                                    'en',
                                    'nl-nl',
                                ) || 0,
                        })}
                    </div>
                }
                withArrow
            >
                <div className={overviewStyles.centerItems}>{statusIcon}</div>
            </Tooltip>
            {units.length > 0 && ![InvoiceType.Split].includes(props.item.type) ? (
                <AccountingSubmissionPreview item={props.item} reset={() => props.onUpdateItem(props.item)} />
            ) : (
                <div></div>
            )}
            <Link to={`/invoice/${props.item.id}/edit`}>{props.item.number}</Link>
            <div>
                <FormattedDateRange fromDate={props.item.date} toDate={props.item.dueDate} date={props.item.date} format={DateFormats.Full} />
            </div>
            <InvoiceListItemTitle {...props} />
            <InvoiceListItemTurnover {...props} />

            {listItemMedium && (
                <MoneyText
                    value={{
                        currency: props.item.price.currency,
                        amount: props.item.price.vat
                            .map(vat => parseNumber(vat.value, 'en'))
                            .reduce((a, b) => a + b, 0)
                            .toString(),
                    }}
                    textProps={{
                        size: 300,
                    }}
                    align="right"
                />
            )}
            {listItemMedium && (
                <MoneyText
                    value={{
                        currency: props.item.outstandingPrice?.currency || props.item.price.currency,
                        amount: props.item.outstandingPrice?.total || '0.00',
                    }}
                    textProps={{
                        size: 300,
                    }}
                    align="right"
                />
            )}

            <Tooltip relationship={'label'} content={props.item.files?.map(file => `${file.reference}: ${file.name}`).join(', ')} withArrow>
                <Link key={props.item.id} to={`/file/${props.item.files?.[0]?.id}/time`} className={overviewStyles.alignItemsEnd}>
                    <Text size={200}>
                        {props.item.files?.[0]?.reference} {props.item.files?.length > 1 ? `+${props.item.files?.length - 1}` : ''}
                    </Text>
                </Link>
            </Tooltip>
            <InvoiceNoteDialog invoice={props.item} onSubmit={value => (props.item.note = value)} />
        </>
    )
}
